// Facebook Pixel tracking utilities and Google Analytics event tracking
// Note: GA4 setup is now handled by @next/third-parties/google GoogleAnalytics component

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
  }
}

export interface LeadTrackingData {
  campaign: string;
  email?: string;
  companyName?: string;
  industry?: string;
  companySize?: string;
  monthlyVolume?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
}

/**
 * Google Analytics 4 event tracking functions
 * Note: Basic GA4 setup and automatic page views are handled by @next/third-parties GoogleAnalytics
 * @lintignore - Accessed via aggregated `analytics` object, not direct imports
 */
export const ga4 = {
  // Track form views
  trackFormView: (
    data: Pick<LeadTrackingData, 'campaign' | 'utmSource' | 'utmMedium' | 'utmCampaign'>,
  ) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_view', {
        event_category: 'engagement',
        event_label: data.campaign,
        campaign_name: data.utmCampaign,
        campaign_source: data.utmSource,
        campaign_medium: data.utmMedium,
        custom_parameters: {
          form_type: 'lead_registration',
          campaign: data.campaign,
        },
      });
    }
  },

  // Track form field interactions
  trackFormInteraction: (data: {
    campaign: string;
    field: string;
    action: 'focus' | 'blur' | 'change';
  }) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_interaction', {
        event_category: 'engagement',
        event_label: `${data.campaign}_${data.field}_${data.action}`,
        custom_parameters: {
          form_type: 'lead_registration',
          campaign: data.campaign,
          field_name: data.field,
          interaction_type: data.action,
        },
      });
    }
  },

  // Track form submissions
  trackFormSubmission: (data: LeadTrackingData) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_submit', {
        event_category: 'conversion',
        event_label: data.campaign,
        campaign_name: data.utmCampaign,
        campaign_source: data.utmSource,
        campaign_medium: data.utmMedium,
        custom_parameters: {
          form_type: 'lead_registration',
          campaign: data.campaign,
          company_industry: data.industry,
          company_size: data.companySize,
          monthly_volume: data.monthlyVolume,
        },
      });
    }
  },

  // Track successful lead generation (conversion)
  trackLeadGeneration: (data: LeadTrackingData) => {
    if (typeof window !== 'undefined' && window.gtag) {
      // Standard GA4 conversion event
      window.gtag('event', 'generate_lead', {
        event_category: 'conversion',
        event_label: data.campaign,
        value: 1,
        currency: 'USD',
        campaign_name: data.utmCampaign,
        campaign_source: data.utmSource,
        campaign_medium: data.utmMedium,
        custom_parameters: {
          lead_type: 'marketing_qualified',
          campaign: data.campaign,
          company_name: data.companyName,
          company_industry: data.industry,
          company_size: data.companySize,
          monthly_volume: data.monthlyVolume,
        },
      });

      // Note: Custom conversion event would use GA measurement ID if available
    }
  },
};

/**
 * Facebook Pixel tracking functions
 * @lintignore - Accessed via aggregated `analytics` object, not direct imports
 */
export const fbPixel = {
  // Track page views
  pageView: () => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView');
    }
  },

  // Track form views
  trackFormView: (data: Pick<LeadTrackingData, 'campaign' | 'utmSource' | 'utmMedium'>) => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_type: 'form',
        content_name: `lead_form_${data.campaign}`,
        content_category: 'lead_generation',
      });
    }
  },

  // Track form submissions (initiate lead)
  trackFormSubmission: (data: LeadTrackingData) => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: data.campaign,
        content_category: 'lead_form',
        value: 1,
        currency: 'USD',
      });
    }
  },

  // Track successful lead generation
  trackLeadGeneration: (data: LeadTrackingData) => {
    if (typeof window !== 'undefined' && window.fbq) {
      // Standard lead event
      window.fbq('track', 'Lead', {
        content_name: data.campaign,
        content_category: 'lead_generation',
        value: 1,
        currency: 'USD',
        status: 'completed',
      });

      // Complete registration event
      window.fbq('track', 'CompleteRegistration', {
        content_name: data.campaign,
        status: 'completed',
      });
    }
  },
};

/**
 * Combined tracking utility
 */
export const analytics = {
  // Track page views across platforms
  // Note: GA4 page views are now automatic via @next/third-parties GoogleAnalytics
  trackPageView: () => {
    fbPixel.pageView();
  },

  // Track form views
  trackFormView: (
    data: Pick<LeadTrackingData, 'campaign' | 'utmSource' | 'utmMedium' | 'utmCampaign'>,
  ) => {
    ga4.trackFormView(data);
    fbPixel.trackFormView(data);
  },

  // Track form interactions
  trackFormInteraction: (data: {
    campaign: string;
    field: string;
    action: 'focus' | 'blur' | 'change';
  }) => {
    ga4.trackFormInteraction(data);
    // Facebook Pixel doesn't have granular form interaction tracking
  },

  // Track form submissions
  trackFormSubmission: (data: LeadTrackingData) => {
    ga4.trackFormSubmission(data);
    fbPixel.trackFormSubmission(data);
  },

  // Track successful lead generation
  trackLeadGeneration: (data: LeadTrackingData) => {
    ga4.trackLeadGeneration(data);
    fbPixel.trackLeadGeneration(data);
  },
};

/**
 * Utility to extract UTM parameters from URL or search params
 * @lintignore - Accessed via aggregated `analytics` object, not direct imports
 */
export const extractUTMParams = (
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>,
) => {
  const getParam = (key: string) => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.get(key) || undefined;
    }
    const value = searchParams[key];
    return typeof value === 'string' ? value : undefined;
  };

  return {
    utmSource: getParam('utm_source'),
    utmMedium: getParam('utm_medium'),
    utmCampaign: getParam('utm_campaign'),
    utmContent: getParam('utm_content'),
  };
};

/**
 * Debug mode for development
 * @lintignore - Accessed via aggregated `analytics` object, not direct imports
 */
export const debugAnalytics = (event: string, data: any) => {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    // eslint-disable-next-line no-console
    console.log(`[Analytics] ${event}:`, data);
  }
};
